<script setup lang="ts">
import { VehicleAlgoliaRecord } from "../../types";
import { useI18n } from "vue-i18n";
import "vue3-carousel/dist/carousel.css";
import { computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import { useToast } from "vue-toastification";
import Button from "@/Components/Buttons/Button.vue";
import VehicleInfo from "@/Components/VehicleInfo.vue";
import CarouselCard from "@/Components/Cards/CarouselCard.vue";
import { route } from "ziggy-js";
import { useFilters } from "@/helpers/filters";
import { submitCompareFavorite } from "@/marketplace/widgets/compare-favorite";

const { t } = useI18n();

const props = withDefaults(
    defineProps<{
        item: VehicleAlgoliaRecord;
        preserveSearchParams?: boolean;
        isSold?: boolean;
        eagerLoading?: boolean;
        imageLayout?: string;
    }>(),
    {
        preserveSearchParams: false,
        isSold: false,
        eagerLoading: false,
        imageLayout: "default",
    },
);

const page = usePage();
const toast = useToast();
const compareFavorite = computed(
    () => page.props.compareFavorite as { enabled: boolean } | null,
);

const getVehicleUrl = (slug: string, item: VehicleAlgoliaRecord) => {
    const baseUrl = route("vehicles.show", { slug, vehicle: item });

    if (props.preserveSearchParams && window.location.search) {
        return `${baseUrl}${window.location.search}`;
    }

    return baseUrl;
};

const vehicleUrl = computed(() => getVehicleUrl(props.item.slug, props.item));

const { formatCurrency, formatNumber } = useFilters();

const onCompareFavorite = async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const ctx = compareFavorite.value;
    if (!ctx?.enabled) return;

    const vehicleId = Number(props.item.id ?? props.item.objectID);
    if (!Number.isFinite(vehicleId) || vehicleId <= 0) return;

    try {
        await submitCompareFavorite(vehicleId);
        toast.success(t("toast.compareFavoriteAdded"));
    } catch {
        toast.error(t("toast.compareFavoriteError"));
    }
};
</script>

<template>
    <div
        class="vehicle-card"
        :class="{ 'vehicle-card--sold': isSold }"
        data-link="parent"
    >
        <div class="vehicle-card__header">
            <div class="vehicle-card__header__top">
                <h3 class="vehicle-card__title vehicle-card__text">
                    <span class="vehicle-card__text--light">{{
                        item.make + " "
                    }}</span>
                    <a
                        :href="vehicleUrl"
                        data-link="item"
                        class="vehicle-card__subtitle"
                        >{{ item.model }} {{ item.specification }}</a
                    >
                </h3>

                <div v-if="!isSold" class="vehicle-card__header__top__price">
                    <p>
                        <span class="font-weight-semibold"
                            >{{ formatCurrency(item.monthly_price, true) }}
                        </span>
                        <span class="month">{{ t("vehicle.perMonth") }}</span>
                    </p>
                </div>
                <div v-else class="vehicle-card__header__top__badge">
                    {{ t("landingPage.sold") }}
                </div>
            </div>
        </div>

        <div class="vehicle-card__slider">
            <div class="vehicle-card__image-wrap">
                <CarouselCard
                    v-if="item.photos"
                    :url="vehicleUrl"
                    :vehicle="item"
                    :photos="item.photos"
                    :photos-count="item.photos_count"
                    :eager-loading="eagerLoading"
                    :image-layout="imageLayout"
                />
                <button
                    v-if="compareFavorite?.enabled"
                    type="button"
                    class="vehicle-card__compare-favorite"
                    :aria-label="t('Ajouter au comparateur')"
                    @click.stop.prevent="onCompareFavorite"
                >
                    <svg
                        class="vehicle-card__compare-favorite-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="12 11 22 21"
                        width="22"
                        height="21"
                        aria-hidden="true"
                    >
                        <path
                            d="M25.5762 17.9023L25.749 18.25L26.1328 18.3086L32.25 19.25L27.75 23.75L27.8076 24.126L28.75 30.25L23.3467 27.4307L23 27.25L22.6533 27.4307L17.25 30.25L18.1924 24.126L18.25 23.75L13.75 19.25L19.8672 18.3086L20.25 18.25L23 12.75L25.5762 17.9023Z"
                        />
                    </svg>
                </button>
            </div>
        </div>

        <div class="vehicle-card__footer">
            <div class="vehicle-card__footer__info">
                <VehicleInfo
                    :value="item.mileage ? formatNumber(item.mileage) : '-'"
                    unit="km"
                />
                <VehicleInfo :value="item.power" unit="cv" />
                <VehicleInfo :value="item.registration_at" />
            </div>

            <div class="vehicle-card__footer__action">
                <Button as="a" :href="vehicleUrl">{{
                    isSold ? t("vehicle.seeDetails") : t("vehicle.choose")
                }}</Button>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.vehicle-card {
    $self: &;

    display: grid;
    row-gap: 0.5rem;
    padding: 1rem 0.5rem;

    background-color: $grey-card;
    border-radius: 0.25rem;
    &__slider {
        overflow: hidden;
    }

    &__image-wrap {
        position: relative;
    }

    &__compare-favorite {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        z-index: 10;
        appearance: none;
        border: none;
        background: rgba(0, 0, 0, 0.6);
        border-radius: 4px;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        padding: 0;
        transition: transform 150ms, background 150ms;

        .vehicle-card__compare-favorite-icon {
            display: block;
            width: 22px;
            height: 21px;
            flex-shrink: 0;

            path {
                fill: none;
                stroke: white;
                stroke-width: 1.5;
                stroke-linejoin: round;
                transition:
                    fill 150ms ease,
                    stroke 150ms ease;
            }
        }

        &:hover {
            transform: scale(1.05);

            .vehicle-card__compare-favorite-icon {
                path {
                    fill: $primary;
                    stroke: $primary;
                }
            }
        }
    }
    &__text {
        color: $dark-font;

        &--light {
            opacity: 0.5;
        }
    }

    &__title {
        margin: 0;
        display: flex;
        flex-direction: column;
        font-weight: 400;
        font-size: 0.875rem;
        line-height: 1.25rem;
    }

    &__subtitle {
        margin: 0;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        text-decoration: none;
        color: inherit;

        font-weight: 500;
        font-size: 1rem;
        margin-top: 2px;
    }

    &__header {
        overflow: hidden;

        p {
            margin: 0;
        }

        &__top {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2px;
            column-gap: 0.125rem;

            &__price {
                position: absolute;
                right: 10px;
                font-size: 0.875rem;

                .month {
                    color: rgba($dark-font, 0.5);
                    font-weight: 400;
                }
            }

            &__badge {
                position: absolute;
                right: 10px;
                background-color: $dark-font;
                color: #fff;
                font-size: 0.7rem;
                font-weight: 600;
                padding: 0.2rem 0.5rem;
                border-radius: 2px;
                text-transform: uppercase;
            }
        }
    }

    &__footer {
        margin-top: 0.125rem;
        height: 40px;

        &__info {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            height: 100%;
        }

        &__action {
            display: none;
        }
    }

    // SOLD MODE
    &--sold {
        opacity: 0.85;

        #{$self}__slider {
            filter: grayscale(30%);
        }

        &:hover {
            opacity: 1;
        }
    }

    // HOVER STYLES
    @media (hover: hover) {
        &:hover {
            background-color: $dark-font;
            color: #ffffff;

            #{$self}__header {
                &__top {
                    &__price {
                        .month {
                            color: rgba(white, 0.5);
                        }
                    }

                    &__badge {
                        background-color: #fff;
                        color: $dark-font;
                    }
                }
            }

            #{$self}__text {
                color: #ffffff;
            }

            #{$self}__footer {
                &__info {
                    display: none;
                }

                &__action {
                    display: block;
                }
            }
        }
    }
}
</style>
